import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, Trash2, Info, Upload, ScanLine, Copy, ExternalLink, Send, AlertTriangle, User, Users } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { PORTAL_T, docChecklist } from "@/lib/portalSubmitI18n";
import SmartScanModal from "@/components/SmartScanModal";
import CountryAutocomplete from "@/components/CountryAutocomplete";
import LandingBackdrop from "@/components/portal/LandingBackdrop";
import { api } from "@/api/client";

const PORTAL_API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;

const COUNTRIES = [
  "إيطاليا", "فرنسا", "إسبانيا", "ألمانيا", "المملكة المتحدة",
  "الولايات المتحدة", "كندا", "اليونان", "هولندا", "البرتغال",
];
const VISA_TYPES = ["سياحة", "زيارة عائلية", "أعمال", "علاج", "دراسة", "Schengen", "دعوة"];
const OTHER = "__other__";

const emptyForm = {
  client_full_name: "",
  client_mobile_number: "",
  nationality_type: "saudi",
  applicant_type: "individual",
  marital_status: "single",
  country: "",
  country_code: "",   // ISO-2 selected from the autocomplete (empty when free text)
  country_other: "",
  visa_type: "",
  visa_type_other: "",
  travel_date: "",
  return_date: "",
  iqama_expiry: "",  // ISO date — required for non-Saudi applicants
  // Biometric / fingerprint city — MANDATORY for every applicant. One of
  // "riyadh" | "jeddah" | "dammam" (trilingual labels rendered in the UI).
  biometric_city: "",
  // Sponsor / employer for the MAIN applicant only. The kafil (guarantor)
  // of any domestic worker travelling with them is the applicant himself —
  // so no separate sponsor is needed for companions. Optional field.
  sponsor_name: "",
  sponsor_type: "individual",  // individual | company
  // Saudi National Address — building number + district + city + postcode.
  // Some consulates explicitly request it for proof of residence.
  national_address: "",
  members: [],
  // Domestic-worker companions (سائق / خادمة / طباخة / …). The applicant
  // ticks "yes" and chooses how many companions, then fills a card per
  // companion (job title is FREE TEXT, no dropdown). Companions appear as
  // members in the office workspace, with relationship_to_main="companion"
  // and a free-text `job_title`.
  has_companion_workers: false,
  companion_workers: [],   // [{ full_name, job_title, nationality, passport_number, iqama_number, date_of_birth, sex }]
  translation_service: false,
  // When translation_service is true:
  //   translation_self_provided=true  → client uploads their own translation (no fee)
  //   translation_self_provided=false → office translates (+50 SAR auto-applied)
  translation_self_provided: false,
  muaqqib_service: false,
  muaqqib_persons_count: 0,
  // Indexes of family members the client wants Muaqqib service for. The
  // selection is captured here and locked once the application is submitted
  // — the office charges 100 SAR (catalog) × len(this list).
  muaqqib_selected_indexes: [],
  policy_agreed: false,
};

export default function PortalSubmit({ mode = "portal" } = {}) {
  const { theme, dir, lang } = useI18n();
  const T = PORTAL_T[lang];
  const navigate = useNavigate();
  const isOffice = mode === "office";
  // Stages by mode: portal renders 4 (basic + travelers_docs combined + addons + policy).
  // Office skips travelers_docs+policy because the employee already gathered them.
  const stageOrder = isOffice
    ? ["basic", "travelers", "addons"]
    : ["basic", "travelers_docs", "addons", "policy"];
  const STAGES = stageOrder.length;
  const LS_KEY = isOffice ? "vta_office_draft_v1" : "vta_portal_draft_v2";
  const [stage, setStage] = useState(1);
  const stageKey = stageOrder[stage - 1];
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) return { ...emptyForm, ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return emptyForm;
  });
  // Files stay LOCAL only — never sent to the server until final submit.
  // Shape: { [composite_key]: { file, name, size, req_key, member_ref } }
  //   composite_key examples:
  //     "passport_scan::main"       — single requirement, main applicant
  //     "passport_scan::<uuid>"     — same requirement for a specific family member
  //     "personal_photos::main::1"  — multi-file (count>1), index 1
  const [localFiles, setLocalFiles] = useState({});
  // Free-text/date answers for non-file dynamic requirements
  //   Shape: { [req_key + "::" + member_ref]: <string value> }
  const [dynAnswers, setDynAnswers] = useState({});
  // Dynamic requirements pulled from the backend on mount
  const [dynReqs, setDynReqs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [createdApp, setCreatedApp] = useState(null); // office mode — shows the tracking link card

  // Fetch the dynamic checklist once. Falls back silently if the API is
  // unreachable — Stage 3 then just shows nothing (better than crashing).
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/document-requirements`)
      .then((r) => setDynReqs(r.data?.items || []))
      .catch(() => setDynReqs([]));
  }, []);

  // Auto-save text data only (NOT files — files are not serializable to LS)
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form, LS_KEY]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // When the user switches to family/multiple mode and has no members yet,
  // seed the list with the main applicant so they don't have to re-type it.
  useEffect(() => {
    if (form.applicant_type !== "family" && form.applicant_type !== "multiple") return;
    if (form.members.length > 0) return;
    if (!form.client_full_name?.trim()) return;
    setForm((s) => ({
      ...s,
      members: [{
        full_name: s.client_full_name,
        passport_number: "",
        nationality: "",
        nationality_type: s.nationality_type, // per-applicant in MULTIPLE
        relationship_to_main: s.applicant_type === "multiple" ? "applicant" : "self",
      }],
    }));
  }, [form.applicant_type, form.client_full_name, form.members.length, form.nationality_type]);

  const validateStage = () => {
    setErr("");
    // Specific-field-by-field validation — never throw a generic "this field
    // is required" because the client can't tell WHICH field is missing and
    // the form appears to silently hang.
    const missing = (field) => {
      const labels = {
        client_full_name: T.full_name || "الاسم الكامل",
        client_mobile_number: T.mobile || "رقم الجوال",
        country: T.country_label || "الدولة المطلوبة",
        visa_type: T.visa_type_label || "نوع التأشيرة",
        biometric_city: "مدينة البصمة",
        member_name: T.member_name || "اسم المرافق",
      };
      return `${T.err_required || "الحقل مطلوب"}: ${labels[field] || field}`;
    };
    if (stageKey === "basic") {
      if (!form.client_full_name?.trim()) { setErr(missing("client_full_name")); return false; }
      if (!form.client_mobile_number?.trim()) { setErr(missing("client_mobile_number")); return false; }
      const country = form.country === OTHER ? form.country_other?.trim() : form.country;
      const visa = form.visa_type === OTHER ? form.visa_type_other?.trim() : form.visa_type;
      if (!country) { setErr(missing("country")); return false; }
      if (!visa) { setErr(missing("visa_type")); return false; }
      if (!form.biometric_city) { setErr(missing("biometric_city")); return false; }
      // Travel date sanity — must be today or later. Browser already blocks
      // via `min` attr but server-side hand-typed dates can sneak through.
      if (form.travel_date) {
        const td = new Date(form.travel_date); td.setHours(23,59,59,999);
        if (td < new Date()) {
          setErr("تاريخ السفر في الماضي — لا يمكن قبول معاملة بتاريخ منتهٍ. يرجى اختيار تاريخ مستقبلي.");
          return false;
        }
      }
      // Return date (if filled) cannot be before travel date.
      if (form.return_date && form.travel_date && new Date(form.return_date) < new Date(form.travel_date)) {
        setErr("تاريخ العودة قبل تاريخ السفر — يرجى التحقق من التواريخ.");
        return false;
      }
      // Iqama expiry is mandatory for non-Saudi applicants AND must be in
      // the future (otherwise the visa application is doomed to fail).
      if (form.nationality_type === "non_saudi") {
        if (!form.iqama_expiry) {
          setErr(`${T.err_required || "الحقل مطلوب"}: تاريخ انتهاء الإقامة`);
          return false;
        }
        const exp = new Date(form.iqama_expiry); exp.setHours(23,59,59,999);
        if (exp < new Date()) {
          setErr("تاريخ انتهاء الإقامة في الماضي — يرجى تجديد الإقامة من «أبشر» أولاً ثم إعادة التقديم.");
          return false;
        }
      }
    }
    // Companion-worker iqama expiry — REQUIRED for every foreign worker
    // (driver, maid, cook...) listed in Stage 2.
    if (form.has_companion_workers && (form.companion_workers || []).length > 0) {
      for (let i = 0; i < form.companion_workers.length; i++) {
        const w = form.companion_workers[i];
        if (!w.iqama_expiry) {
          setErr(`تاريخ انتهاء إقامة العامل #${i + 1} مطلوب — تحقّق من أبشر/توكلنا.`);
          return false;
        }
        const we = new Date(w.iqama_expiry); we.setHours(23,59,59,999);
        if (we < new Date()) {
          setErr(`إقامة العامل #${i + 1} منتهية — يرجى تجديدها أولاً قبل التقديم.`);
          return false;
        }
      }
    }
    if ((stageKey === "travelers" || stageKey === "travelers_docs") && (form.applicant_type === "family" || form.applicant_type === "multiple")) {
      if (form.members.length === 0) { setErr(missing("member_name")); return false; }
      // Hard cap at 15 applicants in MULTIPLE mode (business rule)
      if (form.applicant_type === "multiple" && form.members.length > 15) {
        setErr(T.err_max_15 || "الحد الأقصى 15 متقدماً للمجموعة"); return false;
      }
      // Only the name is required per member.
      for (let i = 0; i < form.members.length; i++) {
        if (!form.members[i].full_name?.trim()) {
          setErr(`${T.err_required || "الحقل مطلوب"}: ${T.member_name || "اسم المرافق"} #${i + 1}`);
          return false;
        }
      }
    }
    // Validate companion workers — only the JOB TITLE is mandatory at this
    // stage. The companion's name comes automatically from the passport
    // Smart Scan in the next step, so we don't force the client to type it.
    if (stageKey === "travelers" || stageKey === "travelers_docs") {
      if (form.has_companion_workers) {
        const ws = form.companion_workers || [];
        if (ws.length === 0) { setErr("أضف مرافقاً واحداً على الأقل أو اضغط لا"); return false; }
        for (let i = 0; i < ws.length; i++) {
          const w = ws[i];
          if (!w?.job_title?.trim()) {
            setErr(`الوظيفة للمرافق #${i + 1} مطلوبة (خادمة / سائق / طباخ ...)`); return false;
          }
        }
      }
    }
    if (stageKey === "docs" || stageKey === "travelers_docs") {
      // Dynamic checklist (Iteration 26): build the live list of (req, member) rows
      const rows = resolveDynamicChecklist(dynReqs, form, "docs");
      const missing = [];
      for (const row of rows) {
        if (!row.req.required) continue;
        if (row.req.type === "file") {
          const need = row.req.count || 1;
          const have = Object.keys(localFiles).filter((k) =>
            k.startsWith(`${row.composite_key}::`) || k === row.composite_key
          ).length;
          if (have < need) missing.push(`${row.req.label_ar} (${row.member_name})`);
        } else {
          const v = (dynAnswers[row.composite_key] || "").trim();
          if (!v) missing.push(`${row.req.label_ar} (${row.member_name})`);
        }
      }
      if (missing.length > 0) {
        setErr(`${T.err_required}: ${missing.join(" • ")}`);
        return false;
      }
    }
    if (stageKey === "policy" && !form.policy_agreed) { setErr(T.must_agree); return false; }
    if (stageKey === "docs" || stageKey === "travelers_docs") {
      const people = [...(form.members || []), ...(form.has_companion_workers ? (form.companion_workers || []) : [])];
      for (let i = 0; i < people.length; i++) {
        const person = people[i] || {};
        const docNumber = person.passport_number || person.national_id || person.iqama_number;
        const complete = (person.full_name_en || person.full_name_ar || person.full_name) && docNumber && person.date_of_birth && person.expiry_date && person.nationality && person.sex;
        if (!complete) {
          setErr("بيانات الشخص رقم " + (i + 1) + " غير مكتملة: يلزم الاسم ورقم الوثيقة والجنسية وتاريخ الميلاد وتاريخ الانتهاء والجنس. استخدم القارئ أو أكملها يدوياً.");
          return false;
        }
        const expiry = new Date(person.expiry_date);
        if (Number.isNaN(expiry.getTime()) || expiry <= new Date()) {
          setErr("تاريخ انتهاء وثيقة الشخص رقم " + (i + 1) + " غير صالح أو منتهٍ.");
          return false;
        }
      }
    }
    return true;
  };

  const next = async () => {
    if (!validateStage()) return;
    if (stage === STAGES) {
      await finalize();
      return;
    }
    setStage((s) => Math.min(STAGES, s + 1));
  };
  const back = () => { setErr(""); setStage((s) => Math.max(1, s - 1)); };

  const finalize = async () => {
    setBusy(true); setErr("");
    try {
      const resolvedCountry = form.country === OTHER ? form.country_other?.trim() : form.country;
      const resolvedVisa = form.visa_type === OTHER ? form.visa_type_other?.trim() : form.visa_type;
      const payload = {
        client_full_name: form.client_full_name.trim(),
        client_mobile_number: form.client_mobile_number.trim(),
        sponsor_name: (form.sponsor_name || "").trim() || null,
        sponsor_type: form.sponsor_type || null,
        national_address: (form.national_address || "").trim() || null,
        nationality_type: form.nationality_type,
        applicant_type: form.applicant_type,
        marital_status: form.marital_status,
        country: resolvedCountry || null,
        visa_type: resolvedVisa || null,
        travel_date: form.travel_date || null,
        return_date: form.return_date || null,
        biometric_city: form.biometric_city || null,
        iqama_expiry: form.iqama_expiry || null,
        members: (() => {
          // ──────────────────────────────────────────────────────────────
          // CRITICAL: Build the members[] payload for ALL applicant types,
          // not just families. For an INDIVIDUAL applicant, Smart Scan fills
          // form.members[0] with the OCR-extracted passport number, DOB,
          // expiry, sex, nationality (see applyScanToMember). Previously
          // we discarded that data because we returned an empty array for
          // anything other than family/multiple — the office's reviewers
          // saw NOTHING but the name & phone.
          // ──────────────────────────────────────────────────────────────
          const buildMember = (m) => ({
          full_name: (m.full_name_en || m.full_name_ar || m.full_name || form.client_full_name || "").trim(),
          full_name_ar: (m.full_name_ar || "").trim() || null,
          full_name_en: (m.full_name_en || m.full_name || "").trim() || null,
            passport_number: (m.passport_number || form.passport_number || "").trim() || null,
            national_id: (m.national_id || form.national_id || "").trim() || null,
            nationality: (m.nationality || "").trim() || null,
            nationality_type: m.nationality_type || form.nationality_type,
            relationship_to_main: m.relationship_to_main || "self",
            date_of_birth: m.date_of_birth || form.date_of_birth || null,
            expiry_date: m.expiry_date || form.expiry_date || null,
            sex: m.sex || form.sex || null,
          });
          let baseMembers;
          if (form.applicant_type === "family" || form.applicant_type === "multiple") {
            baseMembers = (form.members || []).map(buildMember);
          } else {
            // Individual mode — send a SINGLE member built from form.members[0]
            // (filled by Smart Scan) merged with the root form fields as a
            // last-resort fallback for any field the user typed manually.
            const m = (form.members && form.members[0]) || {};
            baseMembers = [buildMember({ ...m, relationship_to_main: "self" })];
          }
          const companions = (form.has_companion_workers && Array.isArray(form.companion_workers))
            ? form.companion_workers
              // Job title is the only mandatory companion field at submit
              // time. Names are populated by Gemini Vision OCR during the
              // Smart Scan step that the office walks the client through.
              .filter((w) => (w.job_title || "").trim())
              .map((w) => ({
                full_name: (w.full_name || "").trim() || `مرافق - ${w.job_title}`,
                passport_number: (w.passport_number || "").trim() || null,
                iqama_number: (w.iqama_number || "").trim() || null,
                nationality: (w.nationality || "").trim() || null,
                date_of_birth: w.date_of_birth || null,
                expiry_date: w.expiry_date || null,
                sex: w.sex || null,
                job_title: (w.job_title || "").trim() || null,
                relationship_to_main: "companion",
                is_companion: true,
              }))
            : [];
          return [...baseMembers, ...companions];
        })(),
        has_companion_workers: !!form.has_companion_workers,
        translation_service: !!form.translation_service,
        translation_self_provided: !!form.translation_self_provided,
        muaqqib_service: !!form.muaqqib_service,
        muaqqib_persons_count: Number(form.muaqqib_persons_count || 0),
        // Indexes of family members the client chose Muaqqib service for.
        // The office uses this to build a per_selected_person pricing line so
        // the breakdown is auditable later: «Muaqqib for: Walid, Saud, Amjad».
        muaqqib_selected_indexes: Array.isArray(form.muaqqib_selected_indexes)
          ? form.muaqqib_selected_indexes
          : [],
      };

      const fd = new FormData();
      // Embed dynamic free-text/date answers into the payload's `form_data.extras`
      // so they are stored alongside the basic form fields.
      const enrichedPayload = { ...payload, dyn_answers: dynAnswers };
      fd.append("payload_json", JSON.stringify(enrichedPayload));
      if (!isOffice) fd.append("policy_version", "1");

      // Each entry's composite key encodes the requirement key + the member ref:
      //   "passport_scan::main"  →  file_type=passport_scan, member_ref=main
      //   "passport_scan::member_1::0" → multi-file index for "passport_scan" on member_1
      Object.entries(localFiles).forEach(([compositeKey, entry]) => {
        const parts = compositeKey.split("::");
        const reqKey = parts[0];
        const memberRef = parts[1] || "main";
        fd.append("files", entry.file, entry.name);
        fd.append("file_types", reqKey);
        fd.append("file_member_refs", memberRef);
      });

      let data;
      if (isOffice) {
        // Use authenticated workspace endpoint — multipart form
        const resp = await api.post("/workspace/applications/create-on-behalf", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = resp.data;
      } else {
        const resp = await axios.post(`${PORTAL_API}/submit-complete`, fd);
        data = resp.data;
      }
      try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
      setLocalFiles({});
      if (isOffice) {
        setCreatedApp({
          id: data.id,
          application_number: data.application_number,
          client_mobile_number: data.client_mobile_number || form.client_mobile_number.trim(),
        });
      } else {
        navigate(`../success/${data.application_number}`);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "ar" ? "تعذّر الإرسال، حاول مرة أخرى." : "Submission failed, please retry."));
    } finally { setBusy(false); }
  };

  const css = makeStyles(theme);

  // Office mode success card — show the tracking link to send to the client
  if (isOffice && createdApp) {
    return (
      <OfficeSuccessCard
        app={createdApp}
        css={css}
        theme={theme}
        lang={lang}
        onNavigateDetail={() => navigate(`/applications/${createdApp.id}`)}
        onStartAnother={() => {
          setCreatedApp(null);
          setForm(emptyForm);
          setStage(1);
          setLocalFiles({});
        }}
      />
    );
  }

  return (
    <div dir={dir} style={css.page} data-testid="portal-submit">
      <LandingBackdrop theme={theme} variant="ambient" />
      {/* Always-visible back link to the portal landing — so a client who
          opened /submit by mistake can return home without losing context. */}
      <button
        type="button"
        onClick={() => navigate("/portal")}
        style={css.backToPortal}
        data-testid="btn-back-to-portal"
      >
        <span style={{ fontSize: 16, fontWeight: 900 }}>{dir === "rtl" ? "→" : "←"}</span>
        <span>{lang === "ar" ? "العودة إلى البوابة" : lang === "ur" ? "پورٹل پر واپس" : "Back to portal"}</span>
      </button>

      {isOffice && (
        <div style={css.officeBanner} data-testid="office-mode-banner">
          <Send size={14} strokeWidth={2.4} />
          <span>
            {lang === "ar"
              ? "تقديم نيابةً عن العميل — يتم إنشاء معاملة مكتبية ثم إرسال رابط التتبع للعميل."
              : "Filing on behalf of the client — creates an office case, then send the tracking link to the client."}
          </span>
        </div>
      )}
      <Stepper stage={stage} total={STAGES} theme={theme} />

      <div style={css.card} data-testid={`stage-${stage}`}>
        {stageKey === "basic" && <Stage1 form={form} update={update} T={T} css={css} lang={lang} />}
        {stageKey === "travelers" && <Stage2 form={form} setForm={setForm} T={T} css={css} />}
        {stageKey === "docs" && (
          <Stage3
            form={form} T={T} css={css}
            localFiles={localFiles}
            onSetFile={(k, entry) => setLocalFiles((s) => ({ ...s, [k]: entry }))}
            onRemoveFile={(k) => setLocalFiles((s) => { const c = { ...s }; delete c[k]; return c; })}
            setForm={setForm}
            dynReqs={dynReqs}
            dynAnswers={dynAnswers}
            setDynAnswers={setDynAnswers}
            lang={lang}
          />
        )}
        {stageKey === "travelers_docs" && (
          <>
            <Stage2 form={form} setForm={setForm} T={T} css={css} combinedTitle={T.s_combined_title} />
            <div style={{ height: 18 }} />
            <Stage3
              form={form} T={T} css={css}
              localFiles={localFiles}
              onSetFile={(k, entry) => setLocalFiles((s) => ({ ...s, [k]: entry }))}
              onRemoveFile={(k) => setLocalFiles((s) => { const c = { ...s }; delete c[k]; return c; })}
              setForm={setForm}
              dynReqs={dynReqs}
              dynAnswers={dynAnswers}
              setDynAnswers={setDynAnswers}
              lang={lang}
              hideTitle
            />
          </>
        )}
        {stageKey === "addons" && <Stage4 form={form} update={update} T={T} css={css} />}
        {stageKey === "policy" && <Stage5 form={form} update={update} T={T} css={css} />}
      </div>

      {err && <div style={css.err} data-testid="submit-error">{err}</div>}

      <div style={css.nav}>
        <button onClick={back} disabled={stage === 1 || busy} style={css.btnGhost} data-testid="btn-back">
          {T.back}
        </button>
        <button onClick={next} disabled={busy} style={css.btnPrimary} data-testid="btn-next">
          {busy ? "…" : (stage === STAGES
            ? (isOffice ? (lang === "ar" ? "إنشاء المعاملة" : "Create application") : T.submit_app)
            : T.next)}
        </button>
      </div>
    </div>
  );
}

/* ────────────────── Stepper ────────────────── */
/** Resolve the LIVE checklist into per-occurrence rows for a SPECIFIC stage.
 *  Each row = (requirement, member_ref) — taking applies_to/nationality/applicant_type into account.
 *  Returns: [{ req, member_ref, member_name, composite_key }] */
function resolveDynamicChecklist(reqs, form, stage = "docs") {
  if (!Array.isArray(reqs) || reqs.length === 0) return [];
  const nat = form.nationality_type;
  const appl = form.applicant_type;
  const out = [];
  const visible = reqs.filter((r) => r.is_active !== false
    && (r.nationality || []).includes(nat)
    && ((r.applicant_type || []).includes(appl) || appl === "multiple")
    && (r.stage || "docs") === stage);

  for (const r of visible) {
    const targets = [];
    if (r.applies_to === "main" || r.applies_to === "both") {
      targets.push({ ref: "main", name: form.client_full_name || "المتقدم الرئيسي" });
    }
    // "selected" is reserved for future per-application member selection by
    // an employee — for now we treat it the same as "members" so STAS can
    // start tagging requirements with it without breaking the portal.
    if ((r.applies_to === "members" || r.applies_to === "both" || r.applies_to === "selected") && (appl === "family" || appl === "multiple")) {
      (form.members || []).forEach((m, idx) => {
        // ────────────────────────────────────────────────────────────────
        // NOTE: in the portal `members[]` are family dependents (wife,
        // children, ...) and are ALWAYS separate from `client_full_name`
        // (the main applicant). So when `applies_to: "both"`, we must give
        // each member their own slot — there is NO duplication with the
        // main. The previous code skipped members[0] under the wrong
        // assumption that it was the same person as `main`, which caused
        // the wife to silently lose her passport scan / national-id slot.
        // ────────────────────────────────────────────────────────────────
        // For MULTIPLE: filter per-member by THEIR nationality (each applicant is independent)
        if (appl === "multiple" && m.nationality_type
            && !(r.nationality || []).includes(m.nationality_type)) return;
        targets.push({ ref: m.id || `member_${idx}`, name: m.full_name || `المتقدم #${idx + 1}` });
      });
    }
    for (const t of targets) {
      out.push({ req: r, member_ref: t.ref, member_name: t.name,
                 composite_key: `${r.key}::${t.ref}` });
    }
  }

  // Companion domestic workers — always require passport + iqama scans per
  // companion, regardless of the dynamic checklist (these are global to any
  // applicant type). We synthesise two file requirements per companion so
  // they render in Stage 3 alongside the main applicant + members.
  if (stage === "docs" && form.has_companion_workers && Array.isArray(form.companion_workers)) {
    form.companion_workers.forEach((w, idx) => {
      // No full_name filter here — the OCR fills the name AFTER the upload,
      // so we must always create the upload slots for every companion the
      // client opted in for, even if `companion_workers[idx]` is still
      // empty at this point.
      const ref = `companion_${idx}`;
      const name = (w?.full_name || "").trim() || `المرافق ${idx + 1}`;
      const jobSuffix = w?.job_title ? ` — ${w.job_title}` : "";
      const baseLabel = `${name}${jobSuffix}`;
      const passReq = {
        key: "companion_passport_scan",
        label_ar: `صورة جواز المرافق (${baseLabel})`,
        label_en: `Companion passport (${baseLabel})`,
        type: "file", required: true,
        smart_scan_map: "passport_number",
        applies_to: "members",
        is_active: true,
      };
      const iqamaReq = {
        key: "companion_iqama_scan",
        label_ar: `صورة إقامة المرافق (${baseLabel})`,
        label_en: `Companion residency (${baseLabel})`,
        type: "file", required: true,
        smart_scan_map: "national_id",
        applies_to: "members",
        is_active: true,
      };
      out.push({ req: passReq, member_ref: ref, member_name: baseLabel,
                 composite_key: `companion_passport_scan::${ref}` });
      out.push({ req: iqamaReq, member_ref: ref, member_name: baseLabel,
                 composite_key: `companion_iqama_scan::${ref}` });
    });
  }

  return out;
}

function Stepper({ stage, total, theme }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 16, padding: "0 4px" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          flex: 1, height: 5, borderRadius: 3,
          background: i + 1 <= stage ? theme.goldGradient : theme.borderSoft,
          transition: "background 200ms",
        }} />
      ))}
    </div>
  );
}

/* ────────────────── Stage 1 ────────────────── */
function Stage1({ form, update, T, css, lang }) {
  const tip = lang === "ar"
    ? {
        title: "يرجى تعبئة البيانات باللغة الإنجليزية",
        body: "السفارات تطلب جميع البيانات باللغة الإنجليزية كما تظهر في جواز السفر — لتفادي أي أخطاء غير مقصودة.",
      }
    : {
        title: "Please fill in English",
        body: "Embassies require all data in English exactly as it appears in the passport — to avoid unintended mistakes.",
      };
  return (
    <>
      <h2 style={css.h2}>{T.s1_title}</h2>
      <div style={css.tipBox} data-testid="english-fill-tip">
        <Info size={18} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 4 }}>{tip.title}</div>
          <div style={{ fontSize: 12, lineHeight: 1.7, opacity: 0.9 }}>{tip.body}</div>
        </div>
      </div>

      <Field label={T.nationality}>
        <Segmented value={form.nationality_type} onChange={(v) => update("nationality_type", v)}
          options={[
            { v: "saudi", l: T.nationality_saudi },
            { v: "non_saudi", l: T.nationality_non_saudi },
          ]} css={css} testId="seg-nationality" />
      </Field>
      <Field label={T.applicant_type}>
        <Segmented value={form.applicant_type} onChange={(v) => update("applicant_type", v)}
          options={[
            { v: "individual", l: T.applicant_individual },
            { v: "family", l: T.applicant_family },
            { v: "multiple", l: T.applicant_multiple },
          ]} css={css} testId="seg-applicant" />
      </Field>
      <Field label={T.marital}>
        <Segmented value={form.marital_status} onChange={(v) => update("marital_status", v)}
          options={[
            { v: "single", l: T.marital_single },
            { v: "married", l: T.marital_married },
          ]} css={css} testId="seg-marital" />
      </Field>
      <Field label={T.country}>
        <CountryAutocomplete
          lang={lang}
          theme={css.theme || { bg: "transparent", surface: "#fff", text: "inherit", border: "rgba(0,0,0,0.15)" }}
          value={{ code: form.country_code || null, free_text: form.country === OTHER ? (form.country_other || "") : null }}
          onChange={(v) => {
            if (v?.code && v?.country) {
              update("country_code", v.code);
              // Persist the Arabic display name so the workspace shows
              // "فرنسا" rather than "FR" — and back-compat is preserved.
              update("country", v.country.name_ar);
              update("country_other", "");
            } else if (v?.free_text !== null && v?.free_text !== undefined) {
              update("country_code", "");
              update("country", OTHER);
              update("country_other", v.free_text);
            } else {
              update("country_code", "");
              update("country", "");
              update("country_other", "");
            }
          }}
          testIdPrefix="input-country"
        />
      </Field>
      <Field label={T.visa_type}>
        <select value={form.visa_type} onChange={(e) => update("visa_type", e.target.value)} style={css.input} data-testid="input-visa-type">
          <option value="">—</option>
          {VISA_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
          <option value={OTHER}>{T.other_typed}</option>
        </select>
        {form.visa_type === OTHER && (
          <input value={form.visa_type_other} onChange={(e) => update("visa_type_other", e.target.value)}
            placeholder={T.visa_type_placeholder} style={{ ...css.input, marginTop: 8 }} data-testid="input-visa-type-other" />
        )}
      </Field>
      <Field label={T.travel_date}>
        <input type="date" value={form.travel_date}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => update("travel_date", e.target.value)} style={css.input} data-testid="input-travel-date" />
      </Field>
      {/* Biometric / fingerprint city — MANDATORY for every applicant.
          Embassies in Saudi Arabia accept biometric collection in three cities
          only: Riyadh, Jeddah, Dammam. Trilingual labels + options. */}
      <Field label={
        lang === "ar" ? "مدينة البصمة (إلزامي) *"
        : lang === "ur" ? "بائیومیٹرک شہر (لازمی) *"
        : "Biometric city (required) *"
      }>
        <select
          value={form.biometric_city || ""}
          onChange={(e) => update("biometric_city", e.target.value)}
          style={css.input}
          data-testid="select-biometric-city"
        >
          <option value="">
            {lang === "ar" ? "— اختر المدينة —"
              : lang === "ur" ? "— شہر منتخب کریں —"
              : "— Select city —"}
          </option>
          <option value="riyadh">
            {lang === "ar" ? "الرياض" : lang === "ur" ? "ریاض" : "Riyadh"}
          </option>
          <option value="jeddah">
            {lang === "ar" ? "جدة" : lang === "ur" ? "جدہ" : "Jeddah"}
          </option>
          <option value="dammam">
            {lang === "ar" ? "الدمام" : lang === "ur" ? "دمام" : "Dammam"}
          </option>
        </select>
        <div style={{
          marginTop: 6, fontSize: 11, opacity: 0.75,
          lineHeight: 1.6,
        }}>
          {lang === "ar" ? "مدينة مركز التأشيرات التي ستذهب إليها لأخذ البصمة."
            : lang === "ur" ? "ویزا سینٹر کا وہ شہر جہاں آپ بائیومیٹرک کے لیے جائیں گے۔"
            : "The visa center city where you will visit to give biometrics."}
        </div>
      </Field>
      {/* Return date — REQUIRED in spirit (not blocking) for non-Saudis incl. companions.
          Shown side-by-side with travel date when applicable. Trilingual labels. */}
      {(form.nationality_type === "non_saudi" || form.has_companion_workers) && (
        <Field label={
          lang === "ar" ? "تاريخ العودة (هام جداً)"
          : lang === "ur" ? "واپسی کی تاریخ (بہت اہم)"
          : "Return date (very important)"
        }>
          <input
            type="date"
            value={form.return_date || ""}
            min={form.travel_date || new Date().toISOString().slice(0, 10)}
            onChange={(e) => update("return_date", e.target.value)}
            style={css.input}
            data-testid="input-return-date"
          />
          <div style={{
            marginTop: 6, padding: "8px 10px",
            background: "rgba(212,175,55,0.10)",
            border: "1px solid rgba(212,175,55,0.4)",
            borderRadius: 8, fontSize: 11, fontWeight: 700,
            color: "#d4af37", lineHeight: 1.6,
            display: "flex", alignItems: "flex-start", gap: 8,
          }}>
            <AlertTriangle size={14} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{lang === "ar"
              ? "هام: يُفضَّل ذكر تاريخ العودة لتفادي أي إشكالات لاحقة. قد يتصل بك المكتب للتأكيد. (اختياري لا يوقف الإرسال)"
              : lang === "ur"
              ? "اہم: بعد میں کسی بھی مسئلے سے بچنے کے لیے واپسی کی تاریخ ذکر کرنا بہتر ہے۔ دفتر تصدیق کے لیے رابطہ کر سکتا ہے۔ (اختیاری — جمع کرانے کو نہیں روکتا)"
              : "Important: Please provide the return date to avoid issues later. The office may call to confirm. (Optional — does not block submission)"}</span>
          </div>
        </Field>
      )}
      {/* Iqama expiry — MANDATORY for non-Saudi applicants. Friendly reminder
          asks the client to verify via Absher / Tawakkalna QR. */}
      {form.nationality_type === "non_saudi" && (
        <Field label={
          lang === "ar" ? "تاريخ انتهاء الإقامة (إلزامي) *"
          : lang === "ur" ? "اقامہ کی میعاد ختم ہونے کی تاریخ (لازمی) *"
          : "Iqama expiry date (required) *"
        }>
          <input
            type="date"
            value={form.iqama_expiry || ""}
            min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
            onChange={(e) => update("iqama_expiry", e.target.value)}
            style={css.input}
            data-testid="input-iqama-expiry"
          />
          <div style={{
            marginTop: 6, padding: "8px 10px",
            background: "rgba(220,38,38,0.08)",
            border: "1px solid rgba(220,38,38,0.4)",
            borderRadius: 8, fontSize: 11, fontWeight: 700,
            color: "#dc2626", lineHeight: 1.7,
            display: "flex", alignItems: "flex-start", gap: 8,
          }}>
            <AlertTriangle size={14} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{lang === "ar"
              ? "يرجى التحقق من تاريخ انتهاء الإقامة من خلال تطبيق «أبشر» أو «توكلنا» (يظهر في رمز QR لبطاقة الإقامة). إقامة منتهية أو على وشك الانتهاء قد تمنع إصدار التأشيرة."
              : lang === "ur"
              ? "براہ کرم اپنے اقامہ کی تاریخ Absher یا Tawakkalna ایپ سے تصدیق کریں (اقامہ کے QR کوڈ میں نظر آتی ہے)۔"
              : "Please verify your Iqama expiry from the Absher or Tawakkalna app (visible in the Iqama QR code). An expired or soon-expiring Iqama may block visa issuance."}</span>
          </div>
        </Field>
      )}
      <Field label={T.full_name}>
        <input value={form.client_full_name} onChange={(e) => update("client_full_name", e.target.value)} style={css.input} data-testid="input-full-name" />
      </Field>
      <Field label={`${T.mobile} (${T.mobile_hint})`}>
        <input value={form.client_mobile_number} onChange={(e) => update("client_mobile_number", e.target.value)}
               style={css.input} type="tel" dir="ltr" data-testid="input-mobile" />
      </Field>

      {/* Sponsor / employer — kafil/jihat al-amal (optional). Many embassies
          accept this as proof of stable income. */}
      <Field label={T.sponsor_label || (lang === "ar" ? "الكفيل / جهة العمل (اختياري)" : lang === "ur" ? "کفیل / آجر (اختیاری)" : "Sponsor / Employer (optional)")}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={form.sponsor_type || "individual"}
            onChange={(e) => update("sponsor_type", e.target.value)}
            style={{ ...css.input, flex: "0 0 auto", minWidth: 120 }}
            data-testid="input-sponsor-type"
          >
            <option value="individual">{T.sponsor_individual || (lang === "ar" ? "كفيل (شخص)" : lang === "ur" ? "ذاتی کفیل" : "Personal Sponsor")}</option>
            <option value="company">{T.sponsor_company || (lang === "ar" ? "شركة / جهة عمل" : lang === "ur" ? "کمپنی / آجر" : "Company / Employer")}</option>
          </select>
          <input
            value={form.sponsor_name || ""}
            onChange={(e) => update("sponsor_name", e.target.value)}
            placeholder={T.sponsor_placeholder || (lang === "ar" ? "اكتب اسم الكفيل أو الشركة" : lang === "ur" ? "کفیل یا کمپنی کا نام" : "Sponsor or company name")}
            style={{ ...css.input, flex: 1, minWidth: 180 }}
            maxLength={200}
            data-testid="input-sponsor-name"
          />
        </div>
      </Field>

      {/* Saudi National Address (optional but recommended). Free text — building
          number + district + city + postcode, exactly as on Absher. */}
      <Field label={lang === "ar" ? "العنوان الوطني (اختياري)" : lang === "ur" ? "قومی پتہ (اختیاری)" : "National Address (optional)"}>
        <input
          value={form.national_address || ""}
          onChange={(e) => update("national_address", e.target.value)}
          placeholder={lang === "ar" ? "مثال: 1234 - حي النخيل، الرياض 12345" : lang === "ur" ? "مثال: 1234 - الحي، الرياض 12345" : "e.g. 1234 - Al Nakheel, Riyadh 12345"}
          style={css.input}
          maxLength={300}
          data-testid="input-national-address"
        />
      </Field>
    </>
  );
}

/* ────────────────── Stage 2 ────────────────── */
function Stage2({ form, setForm, T, css, combinedTitle }) {
  const title = combinedTitle || T.s2_title;
  if (form.applicant_type === "individual") {
    return (
      <>
        <h2 style={css.h2}>{title}</h2>
        <p style={css.hint}>{T.s2_hint_individual}</p>
        <div style={css.factSoft}>
          <div style={{ fontWeight: 800 }}>{form.client_full_name}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>{form.client_mobile_number}</div>
        </div>
        <CompanionWorkersBlock form={form} setForm={setForm} T={T} css={css} />
      </>
    );
  }
  const add = () => setForm((s) => ({ ...s, members: [...s.members, { full_name: "", passport_number: "", nationality: "", relationship_to_main: "spouse", date_of_birth: "", expiry_date: "", sex: "" }] }));
  const updateM = (i, k, v) => setForm((s) => {
    const arr = [...s.members]; arr[i] = { ...arr[i], [k]: v };
    return { ...s, members: arr };
  });
  const remove = (i) => setForm((s) => ({ ...s, members: s.members.filter((_, j) => j !== i) }));
  return (
    <>
      <h2 style={css.h2}>{title}</h2>
      <p style={css.hint}>
        {form.applicant_type === "multiple"
          ? (T.s2_hint_multiple || "أضِف من 1 إلى 15 شخصاً مستقلين (ليسوا من نفس العائلة). لكل واحد جنسيته ومستنداته.")
          : T.s2_hint_family}
      </p>
      <p style={{
        fontSize: 12, color: css.gold, marginTop: -6, marginBottom: 12,
        background: "rgba(212,175,55,0.08)", padding: "8px 12px",
        borderRadius: 8, lineHeight: 1.7, border: `1px dashed ${css.border}`,
      }}>
        {form.applicant_type === "multiple"
          ? "يمكنك تقديم حتى 15 متقدماً معاً. لكل شخص مستنداته الخاصة في الخطوة التالية."
          : (T.family_simple_note || "اكتب فقط اسم الفرد وصلة القرابة — باقي البيانات تُقرأ تلقائياً من صورة الجواز في الخطوة التالية، أو يكملها الفريق لاحقاً.")}
      </p>
      {form.members.map((m, i) => (
        <div key={i} style={css.memberCard}>
          <div style={css.memberHead}>
            <span style={{ fontWeight: 800 }}>#{i + 1}</span>
            <button onClick={() => remove(i)} style={css.removeBtn} data-testid={`member-remove-${i}`}>
              <Trash2 size={11} strokeWidth={2.3} /> {T.remove}
            </button>
          </div>
          <Field label={T.member_name}>
            <input value={m.full_name || ""} onChange={(e) => updateM(i, "full_name", e.target.value)} style={css.input} data-testid={`member-name-${i}`} />
          </Field>
          <Field label={T.relationship}>
            <select value={m.relationship_to_main || "spouse"} onChange={(e) => updateM(i, "relationship_to_main", e.target.value)} style={css.input} data-testid={`member-relation-${i}`}>
              <option value="self">{T.relation_self || "نفسه/مقدّم الطلب"}</option>
              <option value="spouse">{T.relation_spouse}</option>
              <option value="child">{T.relation_child}</option>
              <option value="parent">{T.relation_parent}</option>
              <option value="sibling">{T.relation_sibling}</option>
              <option value="other">{T.relation_other}</option>
            </select>
          </Field>

          {/* Per-applicant nationality — ONLY in MULTIPLE mode. Each person
              in a multiple submission can be a Saudi citizen OR a resident,
              independent of the main applicant. That drives the doc checklist
              for them (ID vs Iqama, etc.). */}
          {form.applicant_type === "multiple" && (
            <Field label="حالة هذا المتقدم">
              <select
                value={m.nationality_type || form.nationality_type || "saudi"}
                onChange={(e) => updateM(i, "nationality_type", e.target.value)}
                style={css.input}
                data-testid={`member-nationality-type-${i}`}
              >
                <option value="saudi">{T.nationality_saudi}</option>
                <option value="non_saudi">{T.nationality_non_saudi}</option>
              </select>
            </Field>
          )}

          {/* Optional extra details — collapsed by default. The portal does NOT
              require these fields; the office CRM (or Smart Scan OCR) fills
              them later. Surfaced for power users who want to type them now. */}
          <details style={{ marginTop: 4, marginBottom: 4 }} data-testid={`member-extra-toggle-${i}`}>
            <summary style={{
              cursor: "pointer", fontSize: 11, color: css.textMuted,
              padding: "4px 0", fontWeight: 700, listStyle: "none",
            }}>
              {T.extra_fields_optional || "+ بيانات إضافية (اختيارية)"}
            </summary>
            <div style={{ marginTop: 8 }}>
              <Field label={`${T.passport_no} (${T.optional})`}>
                <input value={m.passport_number || ""} onChange={(e) => updateM(i, "passport_number", e.target.value.toUpperCase())} style={css.input} dir="ltr" data-testid={`member-passport-${i}`} />
              </Field>
              <Field label={`${T.member_nationality} (${T.optional})`}>
                <input value={m.nationality || ""} onChange={(e) => updateM(i, "nationality", e.target.value.toUpperCase())} style={css.input} dir="ltr" data-testid={`member-nat-${i}`} />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                <Field label={`${T.member_dob} (${T.optional})`}>
                  <input type="date" value={m.date_of_birth || ""}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => updateM(i, "date_of_birth", e.target.value)} style={css.input} data-testid={`member-dob-${i}`} />
                </Field>
                <Field label={`${T.member_exp} (${T.optional})`}>
                  <input type="date" value={m.expiry_date || ""}
                    min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                    onChange={(e) => updateM(i, "expiry_date", e.target.value)} style={css.input} data-testid={`member-exp-${i}`} />
                </Field>
                <Field label={`${T.member_sex} (${T.optional})`}>
                  <select value={m.sex || ""} onChange={(e) => updateM(i, "sex", e.target.value)} style={css.input} data-testid={`member-sex-${i}`}>
                    <option value="">—</option>
                    <option value="M">{T.sex_male}</option>
                    <option value="F">{T.sex_female}</option>
                  </select>
                </Field>
              </div>
            </div>
          </details>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          onClick={add}
          style={{ ...css.btnGhostFull, width: "100%", opacity: (form.applicant_type === "multiple" && form.members.length >= 15) ? 0.4 : 1 }}
          disabled={form.applicant_type === "multiple" && form.members.length >= 15}
          data-testid="member-add"
        >
          + {T.add_member}
          {form.applicant_type === "multiple" && (
            <span style={{ marginInlineStart: 6, fontSize: 11, opacity: 0.7 }}>
              ({form.members.length}/15)
            </span>
          )}
        </button>
      </div>
      <CompanionWorkersBlock form={form} setForm={setForm} T={T} css={css} />
    </>
  );
}

/* ────────────────── Companion Workers (Domestic helpers) ──────────────────
 * Optional sub-section: "هل لديك عمالة منزلية مرافقة؟" — when YES, the
 * client enters how many companions and a card opens per companion with:
 *   - الاسم الكامل
 *   - الوظيفة (نص حر: سائق / طباخة / خادم / مربية / …)
 *   - الجنسية / رقم الإقامة / رقم الجواز / تاريخ الميلاد / الجنس
 * Companions are sent to the office as members with relationship_to_main =
 * "companion" + job_title, alongside the main applicant + family members.
 */
function CompanionWorkersBlock({ form, setForm, T, css }) {
  const has = !!form.has_companion_workers;
  const list = form.companion_workers || [];

  const setCount = (n) => {
    const target = Math.max(0, Math.min(10, Number(n) || 0));
    setForm((s) => {
      const cur = s.companion_workers || [];
      let next = cur.slice(0, target);
      while (next.length < target) {
        next.push({ full_name: "", job_title: "", nationality: "",
                    passport_number: "", iqama_number: "",
                    date_of_birth: "", sex: "" });
      }
      return { ...s, companion_workers: next };
    });
  };

  const toggle = (yes) => setForm((s) => ({
    ...s,
    has_companion_workers: yes,
    companion_workers: yes ? (s.companion_workers?.length ? s.companion_workers : [
      { full_name: "", job_title: "", nationality: "",
        passport_number: "", iqama_number: "",
        date_of_birth: "", sex: "" },
    ]) : [],
  }));

  return (
    <div style={{
      marginTop: 20, padding: 14, borderRadius: 12,
      background: "rgba(212,175,55,0.05)",
      border: `1px dashed ${css.border}`,
    }} data-testid="companion-workers-block">
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 8, color: css.gold }}>
        {T.companion_q || "هل لديك عمالة منزلية مرافقة؟"}
      </div>
      <p style={{ fontSize: 11, opacity: 0.7, marginTop: 0, marginBottom: 10, lineHeight: 1.7 }}>
        {T.companion_hint || "السائق / الطباخة / الخادم / المربية — أي شخص يسافر معك تحت كفالتك."}
      </p>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button type="button" onClick={() => toggle(true)}
          style={{ ...css.input, flex: 1, cursor: "pointer", padding: "8px 12px",
            background: has ? "rgba(22,163,74,0.15)" : "transparent",
            borderColor: has ? "#16a34a" : undefined,
            fontWeight: has ? 800 : 500, color: has ? "#16a34a" : undefined }}
          data-testid="companion-yes">
          {T.yes || "نعم"}
        </button>
        <button type="button" onClick={() => toggle(false)}
          style={{ ...css.input, flex: 1, cursor: "pointer", padding: "8px 12px",
            background: !has ? "rgba(220,38,38,0.10)" : "transparent",
            borderColor: !has ? "#dc2626" : undefined,
            fontWeight: !has ? 800 : 500, color: !has ? "#dc2626" : undefined }}
          data-testid="companion-no">
          {T.no || "لا"}
        </button>
      </div>

      {has && (
        <>
          <Field label={T.companion_count || "عدد المرافقين"}>
            <input type="number" min={1} max={10}
              value={list.length}
              onChange={(e) => setCount(e.target.value)}
              style={css.input}
              dir="ltr"
              data-testid="companion-count" />
          </Field>

          {list.map((w, i) => (
            <div key={i} style={{
              marginTop: 10, padding: 12, borderRadius: 10,
              background: css.surface, border: `1px solid ${css.border}`,
              display: "flex", flexDirection: "column", gap: 8,
            }} data-testid={`companion-card-${i}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "rgba(212,175,55,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 900, fontSize: 14, color: css.gold,
                  flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <div style={{ minWidth: 0, flex: "0 1 auto" }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: css.gold, whiteSpace: "nowrap" }}>
                    {T.companion_card_title || "مرافق"} #{i + 1}
                  </div>
                </div>
                {/* Job title sits NEXT to #N — name is auto-filled by OCR */}
                <div style={{ flex: 1, minWidth: 120 }}>
                  <input
                    type="text"
                    value={w.job_title || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((s) => {
                        const next = [...(s.companion_workers || [])];
                        next[i] = { ...next[i], job_title: v };
                        return { ...s, companion_workers: next };
                      });
                    }}
                    placeholder="الوظيفة (خادمة / سائق / طباخ ...)"
                    style={{ ...css.input, width: "100%" }}
                    data-testid={`companion-job-${i}`}
                  />
                </div>
              </div>
              <div style={{ fontSize: 11, opacity: 0.7, lineHeight: 1.6, paddingInlineStart: 46 }}>
                {T.companion_docs_hint || "ارفع صور الجواز والإقامة في الخطوة التالية — يقرأها النظام تلقائياً."}
              </div>
              {/* Iqama expiry per worker — MANDATORY (they're foreign workers
                  under your sponsorship). System blocks past dates at input level. */}
              <div style={{ paddingInlineStart: 46, marginTop: 8 }}>
                <label style={{ fontSize: 11, fontWeight: 800, color: "#dc2626", display: "block", marginBottom: 4 }}>
                  تاريخ انتهاء إقامة العامل (إلزامي) *
                </label>
                <input
                  type="date"
                  value={w.iqama_expiry || ""}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((s) => {
                      const next = [...(s.companion_workers || [])];
                      next[i] = { ...next[i], iqama_expiry: v };
                      return { ...s, companion_workers: next };
                    });
                  }}
                  style={{ ...css.input, width: "100%" }}
                  data-testid={`companion-iqama-expiry-${i}`}
                />
                <div style={{ fontSize: 10, color: "#dc2626", marginTop: 4, lineHeight: 1.6 }}>
                  تحقّق من تاريخ انتهاء إقامة العامل من «أبشر» / «توكلنا». إقامة منتهية = لا تأشيرة.
                </div>
              </div>
            </div>
          ))}

          {/* Return date — REQUIRED whenever foreign companion workers are
              involved (Saudi or non-Saudi sponsor). The same field also lives
              on Stage 1 for non-Saudi applicants; we render it here too so
              Saudi sponsors who turn this toggle ON in Stage 2 don't miss it. */}
          <div style={{
            marginTop: 14, padding: 12, borderRadius: 10,
            background: "rgba(212,175,55,0.06)",
            border: `1px solid rgba(212,175,55,0.4)`,
          }} data-testid="companion-return-date-block">
            <Field label="تاريخ العودة (هام جداً)">
              <input
                type="date"
                value={form.return_date || ""}
                min={form.travel_date || new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm((s) => ({ ...s, return_date: e.target.value }))}
                style={css.input}
                data-testid="input-return-date-companion"
              />
            </Field>
            <div style={{
              marginTop: 6, padding: "8px 10px",
              background: "rgba(212,175,55,0.10)",
              border: "1px solid rgba(212,175,55,0.4)",
              borderRadius: 8, fontSize: 11, fontWeight: 700,
              color: "#d4af37", lineHeight: 1.6,
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <AlertTriangle size={14} strokeWidth={2.4} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>تاريخ العودة مطلوب لكل المرافقين تحت كفالتك. قد يتصل بك المكتب للتأكيد.</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ────────────────── Stage 3 — Dynamic checklist (Iteration 26) ──────────────────
 * The checklist is no longer hardcoded — it is fetched from
 * `/api/document-requirements` and STAS can edit it without touching code.
 * Each requirement renders per "applies_to" target (main / each member / both).
 *
 * Iteration 33 — tri-language label support (AR / EN / UR). A small toggle at
 * the top of Stage 3 ONLY lets the client switch the displayed language of the
 * document labels. The selection does NOT affect the rest of the portal
 * (other stages stay in the portal-wide language). Goal: help Urdu speakers
 * understand the document requirements even when they're browsing in AR/EN.
 */
function pickLocale(req, docLang) {
  const lab = req[`label_${docLang}`] || req.label_ar || req.label_en || "";
  const help = req[`helper_${docLang}`] || req.helper_ar || req.helper_en || "";
  return { label: lab, helper: help };
}

function Stage3({ form, T, css, localFiles, onSetFile, onRemoveFile, setForm, dynReqs, dynAnswers, setDynAnswers, lang, hideTitle = false }) {
  const docLang = lang; // Use the global portal language (ar/en/ur) — single source of truth
  const rows = resolveDynamicChecklist(dynReqs, form, "docs");

  // Group rows by member_ref so the UI is organised: a section per person.
  const groups = {};
  for (const row of rows) {
    const key = row.member_ref;
    if (!groups[key]) groups[key] = { name: row.member_name, rows: [] };
    groups[key].rows.push(row);
  }

  /** Smart Scan integration — Iteration 26.
   *  When the OCR returns parsed fields, we look up which dynamic requirement
   *  for THIS member has `smart_scan_map === field` and fill it (member or main). */
  const applyScanToMember = (memberRef, parsed) => {
    if (!parsed) return;
    const map = {
      full_name: parsed.full_name,
      passport_number: parsed.document_number,
      national_id: parsed.document_number,
      nationality: parsed.nationality,
      date_of_birth: parsed.date_of_birth,
      expiry_date: parsed.expiry_date,
      sex: parsed.sex,
    };
    // 1) Fill matching DYNAMIC text/date answers — ALWAYS overwrite with the
    //    freshly-scanned value. Previously we only filled empty fields, which
    //    silently swallowed the OCR result whenever the client had typed
    //    anything (or rescanned). Document fields should reflect the document.
    setDynAnswers((s) => {
      const next = { ...s };
      for (const req of dynReqs) {
        if (!req.smart_scan_map) continue;
        const val = map[req.smart_scan_map];
        if (!val) continue;
        const ck = `${req.key}::${memberRef}`;
        next[ck] = val;
      }
      return next;
    });
    // 2) Update the structured member object too (since Stage 1/2 still hold them)
    //    Use OCR value as the source of truth for document-specific fields.
    if (memberRef === "main") {
      setForm((s) => {
        const arr = [...(s.members || [])];
        const existing = arr[0] || {};
        arr[0] = {
          ...existing,
        full_name: parsed.full_name_en || parsed.full_name_ar || parsed.full_name || existing.full_name || s.client_full_name || "",
        full_name_ar: parsed.full_name_ar || existing.full_name_ar || "",
        full_name_en: parsed.full_name_en || parsed.full_name || existing.full_name_en || "",
          relationship_to_main: existing.relationship_to_main || "self",
          passport_number: parsed.document_number || existing.passport_number || "",
          nationality: parsed.nationality || existing.nationality || "",
          date_of_birth: parsed.date_of_birth || existing.date_of_birth || "",
          expiry_date: parsed.expiry_date || existing.expiry_date || "",
          sex: parsed.sex || existing.sex || "",
        };
        return {
          ...s,
          client_full_name: parsed.full_name || s.client_full_name || "",
          members: arr,
        };
      });
    } else if (memberRef.startsWith("member_")) {
      const idx = Number(memberRef.split("_")[1]);
      setForm((s) => {
        const arr = [...(s.members || [])];
        if (arr[idx]) {
          arr[idx] = {
            ...arr[idx],
          full_name: parsed.full_name_en || parsed.full_name_ar || parsed.full_name || arr[idx].full_name || "",
          full_name_ar: parsed.full_name_ar || arr[idx].full_name_ar || "",
          full_name_en: parsed.full_name_en || parsed.full_name || arr[idx].full_name_en || "",
            passport_number: parsed.document_number || arr[idx].passport_number || "",
            nationality: parsed.nationality || arr[idx].nationality || "",
            date_of_birth: parsed.date_of_birth || arr[idx].date_of_birth || "",
            expiry_date: parsed.expiry_date || arr[idx].expiry_date || "",
            sex: parsed.sex || arr[idx].sex || "",
          };
        }
        return { ...s, members: arr };
      });
    } else if (memberRef.startsWith("companion_")) {
      // Domestic-worker companion — update companion_workers[idx] from OCR.
      // ALL passport fields (number, DOB, expiry, sex) must be persisted —
      // the office needs every datum to process a domestic worker's visa.
      const idx = Number(memberRef.split("_")[1]);
      setForm((s) => {
        const arr = [...(s.companion_workers || [])];
        if (arr[idx]) {
          arr[idx] = {
            ...arr[idx],
          full_name: parsed.full_name_en || parsed.full_name_ar || parsed.full_name || arr[idx].full_name || "",
          full_name_ar: parsed.full_name_ar || arr[idx].full_name_ar || "",
          full_name_en: parsed.full_name_en || parsed.full_name || arr[idx].full_name_en || "",
            passport_number: parsed.document_number || arr[idx].passport_number || "",
            iqama_number: parsed.document_number || arr[idx].iqama_number || "",
            nationality: parsed.nationality || arr[idx].nationality || "",
            date_of_birth: parsed.date_of_birth || arr[idx].date_of_birth || "",
            expiry_date: parsed.expiry_date || arr[idx].expiry_date || "",
            sex: parsed.sex || arr[idx].sex || "",
          };
        }
        return { ...s, companion_workers: arr };
      });
    }
  };

  return (
    <>
      {!hideTitle && <h2 style={css.h2}>{T.s3_title}</h2>}
      <p style={css.hint}>{hideTitle ? (T.s3_hint || "") : T.s3_hint}</p>

      {rows.length === 0 && (
        <div style={{ ...css.hint, padding: 12, background: "rgba(212,175,55,0.05)", borderRadius: 8 }}>
          أكمل الخطوات السابقة (الجنسية + نوع التقديم) لعرض قائمة المتطلبات المخصصة لك.
        </div>
      )}

      {Object.entries(groups).map(([memberRef, group], gIdx) => {
        // Resolve the live member object so we can render editable
        // name + relationship fields right above this person's docs —
        // keeping every applicant's data + documents in ONE card.
        const isMain = memberRef === "main";
        const memberIdx = memberRef.startsWith("member_")
          ? Number(memberRef.split("_")[1])
          : null;
        const companionIdx = memberRef.startsWith("companion_")
          ? Number(memberRef.split("_")[1])
          : null;
        const liveMember = isMain
          ? (form.members?.[0] || { full_name: form.client_full_name, relationship_to_main: "self" })
          : memberIdx !== null
            ? form.members?.[memberIdx] || {}
            : companionIdx !== null
              ? form.companion_workers?.[companionIdx] || {}
              : {};

        const updateMemberField = (k, v) => {
          if (isMain) {
            setForm((s) => {
              const arr = [...(s.members || [])];
              arr[0] = { ...(arr[0] || {}), [k]: v };
              return { ...s, members: arr, ...(k === "full_name" ? { client_full_name: v } : {}) };
            });
          } else if (memberIdx !== null) {
            setForm((s) => {
              const arr = [...(s.members || [])];
              if (arr[memberIdx]) arr[memberIdx] = { ...arr[memberIdx], [k]: v };
              return { ...s, members: arr };
            });
          } else if (companionIdx !== null) {
            setForm((s) => {
              const arr = [...(s.companion_workers || [])];
              if (arr[companionIdx]) arr[companionIdx] = { ...arr[companionIdx], [k]: v };
              return { ...s, companion_workers: arr };
            });
          }
        };

        const cardLabel = isMain
          ? (docLang === "ar" ? "مقدّم الطلب" : docLang === "ur" ? "درخواست گزار" : "Main applicant")
          : companionIdx !== null
            ? (docLang === "ar" ? `العامل المرافق #${companionIdx + 1}` : docLang === "ur" ? `معاون مزدور #${companionIdx + 1}` : `Domestic worker #${companionIdx + 1}`)
            : (docLang === "ar" ? `المرافق #${(memberIdx || 0) + 1}` : docLang === "ur" ? `ساتھی #${(memberIdx || 0) + 1}` : `Companion #${(memberIdx || 0) + 1}`);

        return (
          <div
            key={memberRef}
            data-testid={`stage3-member-card-${gIdx}`}
            style={{
              marginBottom: 16, padding: 16, borderRadius: 14,
              background: css.cardBg || "rgba(255,255,255,0.02)",
              border: `1px solid ${css.border}`,
              boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
            }}
          >
            {/* Card head: number badge + icon + label + person name */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              marginBottom: 14, paddingBottom: 10,
              borderBottom: `1px dashed ${css.border}`,
            }}>
              <div style={{
                minWidth: 32, height: 32, borderRadius: "50%",
                background: "linear-gradient(135deg,#f4d03f,#b8902a)",
                color: "#1a1a1a", fontWeight: 900, fontSize: 14,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>{gIdx + 1}</div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: css.gold }}>
                {isMain ? <User size={16} strokeWidth={2.3} /> : <Users size={16} strokeWidth={2.3} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: 13, color: css.gold, lineHeight: 1.2 }}>
                  {cardLabel}
                </div>
                {liveMember.full_name && (
                  <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {liveMember.full_name}
                  </div>
                )}
              </div>
            </div>

            {/* Inline editable basics — every card carries its own data so
                the client doesn't bounce between stages. Companion-worker cards
                use iqama_number instead of passport_number. */}
            <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              <Field label={isMain ? T.full_name : T.member_name}>
                <input
                  value={liveMember.full_name || ""}
                  onChange={(e) => updateMemberField("full_name", e.target.value)}
                  style={css.input}
                  data-testid={`stage3-name-${gIdx}`}
                />
              </Field>
              {!isMain && companionIdx === null && (
                <Field label={T.relationship}>
                  <select
                    value={liveMember.relationship_to_main || "spouse"}
                    onChange={(e) => updateMemberField("relationship_to_main", e.target.value)}
                    style={css.input}
                    data-testid={`stage3-relation-${gIdx}`}
                  >
                    <option value="spouse">{T.relation_spouse}</option>
                    <option value="child">{T.relation_child}</option>
                    <option value="parent">{T.relation_parent}</option>
                    <option value="sibling">{T.relation_sibling}</option>
                    <option value="other">{T.relation_other}</option>
                  </select>
                </Field>
              )}
            </div>

            {/* Documents + dynamic requirements for THIS person */}
            <div style={css.docList}>
              {group.rows.map((row) => (
                <DynamicReqField
                  key={row.composite_key} row={row} T={T} css={css}
                  localFiles={localFiles}
                  onSetFile={onSetFile} onRemoveFile={onRemoveFile}
                  dynAnswers={dynAnswers} setDynAnswers={setDynAnswers}
                  onScan={(parsed) => applyScanToMember(row.member_ref, parsed)}
                  docLang={docLang}
                />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Renders a single dynamic requirement field (file / text / date / etc). */
function DynamicReqField({ row, T, css, localFiles, onSetFile, onRemoveFile, dynAnswers, setDynAnswers, onScan, docLang = "ar" }) {
  const { req, composite_key } = row;
  const loc = pickLocale(req, docLang);
  const isUrdu = docLang === "ur";
  const labelStyle = isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif", fontSize: 15, lineHeight: 1.8 } : {};
  const dirAttr = docLang === "en" ? "ltr" : "rtl";
  // FILE TYPE — use the existing LocalDocPicker
  if (req.type === "file") {
    const count = req.count || 1;
    if (count === 1) {
      return (
        <LocalDocPicker
          doc={{ key: composite_key, label: loc.label, required: req.required, helper: loc.helper,
                 scannable: !!req.smart_scan_map, mandatoryScan: !!req.mandatory_smart_scan,
                 dir: dirAttr, labelStyle }}
          T={T} css={css}
          current={localFiles[composite_key]}
          onPick={(file, parsed) => {
            // file MAY be null when the user typed/confirmed data without
            // a captured photo (e.g. fallback manual entry). Don't crash.
            const safeName = file?.name || `${composite_key}-scan.jpg`;
            const safeSize = file?.size || 0;
            onSetFile(composite_key, { file: file || null, name: safeName, size: safeSize, scanned: !!parsed });
            if (parsed) onScan(parsed);
          }}
          onRemove={() => onRemoveFile(composite_key)}
        />
      );
    }
    // Multi-file (count > 1) — render N slots with indexed composite keys
    return (
      <div dir={dirAttr}>
        <div style={{ fontSize: 12, marginBottom: 6, opacity: 0.85, ...labelStyle }}>
          {loc.label} <span style={{ opacity: 0.6, fontSize: 10 }}>({count} {docLang === "ur" ? "فائلیں" : docLang === "en" ? "files" : "ملفات"})</span>
        </div>
        {Array.from({ length: count }).map((_, i) => {
          const k = `${composite_key}::${i}`;
          return (
            <LocalDocPicker
              key={k}
              doc={{ key: k, label: `${loc.label} (${i + 1}/${count})`, required: req.required, scannable: false, dir: dirAttr, labelStyle }}
              T={T} css={css}
              current={localFiles[k]}
              onPick={(file) => onSetFile(k, { file, name: file.name, size: file.size })}
              onRemove={() => onRemoveFile(k)}
            />
          );
        })}
      </div>
    );
  }

  // NON-FILE TYPES — text / email / phone / date / number / textarea
  const value = dynAnswers[composite_key] || "";
  const setValue = (v) => setDynAnswers((s) => ({ ...s, [composite_key]: v }));
  const Tag = req.type === "textarea" ? "textarea" : "input";
  const htmlType = req.type === "email" ? "email"
                  : req.type === "phone" ? "tel"
                  : req.type === "date" ? "date"
                  : req.type === "number" ? "number"
                  : "text";
  return (
    <div style={{ marginBottom: 10 }} dir={dirAttr}>
      <label style={{ display: "block", fontSize: 12, marginBottom: 4, ...labelStyle }}>
        {loc.label} {req.required && <span style={{ color: "#dc2626" }}>*</span>}
      </label>
      {loc.helper && (
        <div style={{ fontSize: 10, opacity: 0.6, marginBottom: 4, ...labelStyle }}>{loc.helper}</div>
      )}
      <Tag
        type={Tag === "input" ? htmlType : undefined}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={Tag === "textarea" ? 3 : undefined}
        style={css.input}
        data-testid={`dyn-${composite_key}`}
      />
    </div>
  );
}

function LocalDocPicker({ doc, T, css, current, onPick, onRemove }) {
  const ref = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  // Iteration 26: doc-level `scannable` flag (set when the dynamic
  // requirement has `smart_scan_map`). Falls back to the legacy key-based
  // detection for backward compatibility with the old static list.
  const legacyScannable = ["passport", "national_id", "residency", "iqama",
                            "passport_scan", "national_id_scan", "residence_scan"]
    .some((k) => (doc.key || "").includes(k));
  const scannable = doc.scannable !== undefined ? doc.scannable : legacyScannable;
  const mandatoryScan = !!doc.mandatoryScan;

  // Force-AI read: when the requirement is marked mandatoryScan, we run the
  // AI Vision endpoint directly on the picked file — no Tesseract fallback,
  // no manual upload. If the AI can't read it, we reject the file and ask
  // for a clearer photo. This guarantees the linked fields get filled.
  const docTypeHint = (() => {
    const k = (doc.key || "").toLowerCase();
    if (k.includes("iqama") || k.includes("residen")) return "iqama";
    if (k.includes("national_id")) return "national_id";
    return "passport";
  })();
  const runAiOn = async (file) => {
    setAiBusy(true); setAiErr("");
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "doc.jpg");
      fd.append("doc_type", docTypeHint);
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/portal/ocr/passport`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text() || `HTTP ${res.status}`);
      const data = await res.json();
      const parsed = { ok: true, ...(data.parsed || {}) };
    if (parsed.valid_document === false || !parsed.full_name || !parsed.document_number || !parsed.date_of_birth || !parsed.expiry_date) {
        // OCR couldn't extract any field — but the office still NEEDS the
        // image to process the visa. Attach the file anyway and surface a
        // soft warning so the client knows the auto-read partially failed
        // (the office can re-read manually). Do NOT drop the upload.
        setAiErr("لم تُقبل الصورة لأن البيانات الأساسية لم تُقرأ كاملة. أعد التصوير بوضوح أو أدخل الاسم ورقم الوثيقة وتاريخ الميلاد والانتهاء يدوياً.");
        return;
      }
      onPick(file, parsed);
    } catch (e) {
      // Network / 5xx — still attach the file so the office gets the
      // evidence and can re-read it manually. The error message reassures
      // the client that the upload itself succeeded.
      setAiErr(`تعذّرت قراءة الوثيقة: ${e.message || ""}. أعد المحاولة بصورة واضحة أو أكمل البيانات يدوياً.`);
    } finally {
      setAiBusy(false);
    }
  };

  const onScanResult = ({ mode, file, parsed }) => {
    // After the SmartScanModal closes, persist BOTH the captured photo
    // (when present) AND the parsed JSON so the office gets evidence even
    // when only the manual / AI fallback path succeeded.
    if (parsed && !file) {
      // User confirmed typed data without a photo — record metadata only.
      onPick(null, parsed);
      return;
    }
    if (!file) return;
    // Make a real File object (camera blob has no name)
    const named = file instanceof File
      ? file
      : new File([file], `${doc.key}-scan.jpg`, { type: file.type || "image/jpeg" });
    onPick(named, parsed || null);
  };

  return (
    <div style={css.docItem} dir={doc.dir || undefined}>
      <div style={css.docHead}>
        <span style={{ ...css.docLabel, ...(doc.labelStyle || {}) }}>{doc.label}</span>
        <span style={{ ...css.tag, color: doc.required ? "#dc2626" : "#16a34a" }}>
          {doc.required ? T.required : T.optional}
        </span>
      </div>
      {doc.helper && (
        <div style={{ fontSize: 11, opacity: 0.65, marginTop: -4, marginBottom: 6, ...(doc.labelStyle || {}) }}>
          {doc.helper}
        </div>
      )}
      {current ? (
        <div style={css.docDone} data-testid={`doc-uploaded-${doc.key}`}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Check size={14} strokeWidth={2.6} /> {current.name}
            {current.scanned && (
              <span style={{
                background: "rgba(22,163,74,0.18)", color: "#16a34a",
                padding: "2px 8px", borderRadius: 999, fontSize: 9, fontWeight: 900,
                marginInlineStart: 6,
              }}>قراءة بيانات</span>
            )}
          </span>
          <button onClick={onRemove} style={css.removeBtn}>
            <Trash2 size={11} strokeWidth={2.3} /> {T.delete_file}
          </button>
        </div>
      ) : (
        <>
          {mandatoryScan ? (
            <>
              {/* MANDATORY scan: same UI as the unified Smart Scan modal —
                  the modal itself enforces a valid AI read before
                  closing, so visual consistency is preserved. */}
              <div style={{
                fontSize: 11, color: "#d4af37", marginBottom: 8, fontWeight: 700,
                background: "rgba(212,175,55,0.08)", padding: "6px 10px",
                borderRadius: 8, border: "1px dashed rgba(212,175,55,0.3)",
              }}>
                ⚡ القراءة الذاتية إلزامية لهذه الوثيقة — استخدم الماسح الذكي وسيُعبَّأ كل شيء تلقائياً
              </div>
              <button onClick={() => setScanOpen(true)}
                style={{ ...css.docPick, width: "100%", justifyContent: "center", padding: "12px 14px", fontSize: 13, fontWeight: 800 }}
                data-testid={`doc-mandatory-scan-${doc.key}`}>
                <ScanLine size={16} strokeWidth={2.4} />
                {lang_is_ar() ? "افتح الماسح الذكي" : "Open Smart Scanner"}
              </button>
              <div style={{ fontSize: 10, opacity: 0.55, marginTop: 6 }}>JPG/PNG فقط</div>
            </>
          ) : (
            <>
              {scannable ? (
                <>
                  {/* ONE-STEP "scan + attach". Opening the smart scanner
                      ALWAYS captures the file and attaches it; if OCR
                      succeeds the fields are auto-filled. The user never
                      uploads the same document twice. */}
                  <button onClick={() => setScanOpen(true)}
                    style={{ ...css.docPick, width: "100%", justifyContent: "center", padding: "12px 14px", fontSize: 13, fontWeight: 800 }}
                    data-testid={`doc-scan-${doc.key}`}>
                    <ScanLine size={16} strokeWidth={2.4} />
                    {lang_is_ar() ? "ارفع + اقرأ تلقائياً" : "Upload + auto-read"}
                  </button>
                  <button onClick={() => ref.current?.click()}
                    style={{
                      background: "transparent", border: "none",
                      color: "var(--text-muted, #888)", fontSize: 11,
                      textDecoration: "underline", cursor: "pointer",
                      marginTop: 8, padding: 2,
                    }}
                    data-testid={`doc-pick-fallback-${doc.key}`}>
                    {lang_is_ar() ? "أو ارفع ملف PDF/صورة بدون قراءة" : "Or upload PDF/image without reading"}
                  </button>
                </>
              ) : (
                <button onClick={() => ref.current?.click()}
                  style={{ ...css.docPick, width: "100%", justifyContent: "center" }}
                  data-testid={`doc-pick-${doc.key}`}>
                  <Upload size={14} strokeWidth={2.2} /> {T.drop_file}
                </button>
              )}
              <input
                ref={ref} type="file" accept="image/png,image/jpeg,application/pdf"
                style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f, null); }}
                data-testid={`doc-input-${doc.key}`}
              />
              <div style={{ fontSize: 10, opacity: 0.55, marginTop: 6 }}>{T.formats}</div>
            </>
          )}
        </>
      )}
      <SmartScanModal open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScanResult} docType={doc.key} />
    </div>
  );
}

// Helper: detect language from document
function lang_is_ar() {
  try { return document?.dir === "rtl"; } catch { return true; }
}

/* ────────────────── Stage 4 ────────────────── */
/* iPhone-style top notice — informational text only. The actual yes/no
 * choices live below in the radio cards.  Tri-language via PORTAL_T.
 */
function TranslationNotice({ css, T }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div
      data-testid="translation-notice"
      style={{
        position: "relative",
        marginBottom: 14,
        padding: "14px 16px",
        borderRadius: 16,
        background: "rgba(255,255,255,0.08)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        border: `1px solid ${css.border}`,
        boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
        animation: "iosNotifSlide 0.45s ease-out",
      }}
    >
      <style>{`
        @keyframes iosNotifSlide {
          from { opacity: 0; transform: translateY(-12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: "#16a34a", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "white", fontSize: 18, fontWeight: 900,
        }}>i</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.6, marginBottom: 2 }}>
            {T.tn_brand} • {T.tn_now}
          </div>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>
            {T.tn_title}
          </div>
          <div style={{ fontSize: 12.5, lineHeight: 1.75, opacity: 0.95 }}>
            {T.tn_body}
            <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 8,
              background: "rgba(212,175,55,0.12)", color: css.gold, fontWeight: 700 }}>
              {T.tn_eta}
            </div>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          data-testid="translation-notice-dismiss"
          aria-label="dismiss"
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: css.textMuted, fontSize: 18, lineHeight: 1, padding: 4,
          }}
        >×</button>
      </div>
    </div>
  );
}


function Stage4({ form, update, T, css }) {
  const memberCount = form.applicant_type === "family"
    ? Math.max(form.members?.length || 0, 1)
    : 1;
  // Translation 3-option radio:
  //   "none"   → translation_service=false
  //   "self"   → translation_service=true, translation_self_provided=true
  //   "office" → translation_service=true, translation_self_provided=false
  const translationChoice = !form.translation_service
    ? "none"
    : (form.translation_self_provided ? "self" : "office");
  const setTranslationChoice = (val) => {
    if (val === "none") {
      update("translation_service", false);
      update("translation_self_provided", false);
    } else if (val === "self") {
      update("translation_service", true);
      update("translation_self_provided", true);
    } else {
      update("translation_service", true);
      update("translation_self_provided", false);
    }
  };

  return (
    <>
      <h2 style={css.h2}>{T.s4_title}</h2>

      {/* iPhone-style notification banner about translation requirement */}
      <TranslationNotice css={css} T={T} />

      {/* Translation — 3 options (radio cards) */}
      <div style={css.toggleCard} data-testid="addon-translation">
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 800 }}>{T.translation_service}</div>
          <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.6, marginTop: 4 }}>{T.translation_desc}</div>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {[
            { val: "none",   title: T.translation_choice_none,   hint: T.translation_choice_none_hint },
            { val: "self",   title: T.translation_choice_self,   hint: T.translation_choice_self_hint },
            { val: "office", title: T.translation_choice_office, hint: T.translation_choice_office_hint },
          ].map((opt) => {
            const active = translationChoice === opt.val;
            return (
              <button
                key={opt.val}
                type="button"
                onClick={() => setTranslationChoice(opt.val)}
                data-testid={`addon-translation-${opt.val}`}
                style={{
                  textAlign: "start",
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: active ? css.activeBg : "transparent",
                  border: active ? `2px solid ${css.gold}` : `1px solid ${css.border}`,
                  cursor: "pointer", fontFamily: "inherit",
                  color: css.text,
                  display: "flex", alignItems: "center", gap: 12,
                }}
              >
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 18, height: 18, borderRadius: "50%",
                  border: `2px solid ${active ? css.gold : css.border}`,
                  flex: "0 0 18px",
                }}>
                  {active && (
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: css.gold }} />
                  )}
                </span>
                <span style={{ flex: 1 }}>
                  <div style={{ fontWeight: active ? 900 : 700, fontSize: 13 }}>{opt.title}</div>
                  <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>{opt.hint}</div>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Muaqqib — toggle. For FAMILY the client picks SPECIFIC members so
          the office can later charge exactly N × catalog price (no surprises). */}
      <ToggleCard
        title={T.muaqqib_service}
        desc={T.muaqqib_desc}
        checked={form.muaqqib_service}
        onChange={(v) => {
          update("muaqqib_service", v);
          if (!v) {
            update("muaqqib_persons_count", 0);
            update("muaqqib_selected_indexes", []);
          } else if (form.applicant_type !== "family") {
            // Individual → exactly 1 person, no list to pick from.
            update("muaqqib_persons_count", 1);
            update("muaqqib_selected_indexes", [0]);
          } else {
            // Family → start with nobody selected; client must pick explicitly.
            update("muaqqib_persons_count", 0);
            update("muaqqib_selected_indexes", []);
          }
        }}
        css={css} testId="addon-muaqqib"
      >
        {form.muaqqib_service && form.applicant_type === "family" && (
          <div style={{
            marginTop: 8, padding: "12px 14px",
            background: "rgba(212,175,55,0.06)",
            border: "1px dashed rgba(212,175,55,0.4)",
            borderRadius: 10,
          }} data-testid="muaqqib-family-picker">
            <div style={{ fontSize: 12, fontWeight: 800, color: css.gold, marginBottom: 8 }}>
              حدّد الأشخاص الذين تريد خدمة المعقّب لهم — الاختيار يُقفل بعد الإرسال
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(form.members || []).map((m, idx) => {
                const selected = (form.muaqqib_selected_indexes || []).includes(idx);
                const displayName = (m.full_name || "").trim() || `الفرد رقم ${idx + 1}`;
                return (
                  <label key={idx} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                    background: selected ? "rgba(212,175,55,0.12)" : "transparent",
                    border: `1px solid ${selected ? "rgba(212,175,55,0.5)" : "transparent"}`,
                  }} data-testid={`muaqqib-member-${idx}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={(e) => {
                        const cur = new Set(form.muaqqib_selected_indexes || []);
                        if (e.target.checked) cur.add(idx); else cur.delete(idx);
                        const arr = Array.from(cur).sort((a, b) => a - b);
                        update("muaqqib_selected_indexes", arr);
                        update("muaqqib_persons_count", arr.length);
                      }}
                    />
                    <span style={{ flex: 1, fontWeight: 700 }}>{displayName}</span>
                    {selected && (
                      <span style={{ fontSize: 11, color: css.gold, fontWeight: 800 }}>✓ 100 ر.س</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div style={{
              marginTop: 10, padding: "8px 12px",
              background: "rgba(212,175,55,0.15)", borderRadius: 8,
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontWeight: 900, color: css.gold, fontSize: 13,
            }} data-testid="muaqqib-total">
              <span>عدد المختارين</span>
              <span>{(form.muaqqib_selected_indexes || []).length} × 100 = {(form.muaqqib_selected_indexes || []).length * 100} ر.س</span>
            </div>
          </div>
        )}
        {form.muaqqib_service && form.applicant_type !== "family" && (
          <div style={{
            marginTop: 8, padding: "10px 12px",
            background: "rgba(212,175,55,0.08)", borderRadius: 8,
            fontSize: 12, color: css.gold, lineHeight: 1.7,
          }} data-testid="muaqqib-auto-count">
            {T.muaqqib_price_hint}
            <div style={{ fontWeight: 900, marginTop: 4 }}>
              مسافر واحد × 100 = 100 ر.س
            </div>
          </div>
        )}
      </ToggleCard>
    </>
  );
}

function ToggleCard({ title, desc, checked, onChange, css, children, testId }) {
  return (
    <div style={css.toggleCard} data-testid={testId}>
      <div style={css.toggleHead}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800 }}>{title}</div>
          <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.6, marginTop: 4 }}>{desc}</div>
        </div>
        <label style={css.switch}>
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
          <span style={css.slider(checked)} />
        </label>
      </div>
      {children}
    </div>
  );
}

/* ────────────────── Stage 5 ────────────────── */
function Stage5({ form, update, T, css }) {
  const [disclaimer, setDisclaimer] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/disclaimer`)
      .then((r) => r.json()).then((d) => setDisclaimer(d))
      .catch(() => undefined);
  }, []);
  return (
    <>
      <h2 style={css.h2}>{T.s5_title}</h2>
      <p style={css.policy}>{T.policy_summary}</p>

      <details style={css.policyDetails}>
        <summary style={css.policySummary}>
          {disclaimer?.title_ar ? `${T.read_full} — ${disclaimer.title_ar}` : T.read_full}
        </summary>
        <div style={{ padding: "12px 4px", fontSize: 12, lineHeight: 1.85, opacity: 0.85 }}>
          {disclaimer && (disclaimer.points_ar || []).map((p, i) => (
            <div key={i} style={{ marginBottom: 6 }}>{i + 1}. {p}</div>
          ))}
        </div>
      </details>
      <label style={css.agreeRow} data-testid="agree-checkbox">
        <input type="checkbox" checked={form.policy_agreed} onChange={(e) => update("policy_agreed", e.target.checked)} />
        <span>{T.agree}</span>
      </label>
    </>
  );
}

/* ────────────────── Reusable ────────────────── */
function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <div style={{ fontSize: 11, marginBottom: 6, opacity: 0.7, letterSpacing: "0.4px" }}>{label}</div>
      {children}
    </label>
  );
}

/* ────────────────── Office Success Card ────────────────── */
function OfficeSuccessCard({ app, css, theme, lang, onNavigateDetail, onStartAnother }) {
  const [copied, setCopied] = useState(false);
  const trackUrl = `${window.location.origin}/portal/track/${app.application_number}`;
  // Build a WhatsApp share text + URL — strip "+" and non-digits from mobile.
  // Signoff is fixed per Ministry of Tourism guidance: the exact phrase
  // "مع تحيات وكالة التأشيرات للسفر والسياحة" must appear on every
  // outgoing WhatsApp message.
  const waNum = (app.client_mobile_number || "").replace(/[^\d]/g, "");
  const waText = lang === "ar"
    ? `مرحباً، تم إنشاء معاملتكم لدى مكتب التأشيرات.\nرقم المعاملة: ${app.application_number}\nيمكنكم متابعة الحالة عبر الرابط:\n${trackUrl}\n\nمع تحيات وكالة التأشيرات للسفر والسياحة`
    : `Hello, your visa application has been created.\nApp number: ${app.application_number}\nTrack it here:\n${trackUrl}\n\nBest regards, Visa Travel Agency for Travel & Tourism`;
  const waHref = waNum
    ? `https://wa.me/${waNum}?text=${encodeURIComponent(waText)}`
    : `https://wa.me/?text=${encodeURIComponent(waText)}`;

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(trackUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* ignore */ }
  };
  return (
    <div style={css.page} data-testid="office-success-card">
      <div style={{
        background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`,
        borderRadius: 18, padding: 24, textAlign: "center",
      }}>
        <Check size={56} strokeWidth={2.4} style={{ color: theme.gold, marginBottom: 8 }} />
        <h2 style={{ ...css.h2, textAlign: "center", margin: "8px 0 4px" }}>
          {lang === "ar" ? "تم إنشاء المعاملة بنجاح" : "Application created"}
        </h2>
        <div style={{
          fontFamily: "monospace", direction: "ltr", fontSize: 18, fontWeight: 900,
          color: theme.gold, marginBottom: 16,
        }} data-testid="office-success-appnum">
          {app.application_number}
        </div>
        <div style={{
          background: theme.surface, border: `1px solid ${theme.borderSoft}`,
          borderRadius: 12, padding: 14, marginBottom: 14, textAlign: "start",
        }}>
          <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}>
            {lang === "ar" ? "رابط التتبع للعميل" : "Client tracking link"}
          </div>
          <div style={{
            fontFamily: "monospace", fontSize: 12, direction: "ltr",
            wordBreak: "break-all", color: theme.text,
          }} data-testid="office-track-url">
            {trackUrl}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <button onClick={onCopy} style={{ ...css.btnGhost, flex: 1 }} data-testid="office-copy-link">
            <Copy size={14} strokeWidth={2.4} style={{ verticalAlign: "middle", marginInlineEnd: 6 }} />
            {copied
              ? (lang === "ar" ? "تم النسخ ✓" : "Copied ✓")
              : (lang === "ar" ? "نسخ الرابط" : "Copy link")}
          </button>
          <a
            href={waHref} target="_blank" rel="noreferrer"
            style={{ ...css.btnPrimary, flex: 1, textDecoration: "none", textAlign: "center", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
            data-testid="office-send-whatsapp"
          >
            <ExternalLink size={14} strokeWidth={2.4} />
            {lang === "ar" ? "إرسال عبر واتساب" : "Send via WhatsApp"}
          </a>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={onNavigateDetail} style={{ ...css.btnGhost, flex: 1 }} data-testid="office-open-detail">
            {lang === "ar" ? "فتح المعاملة" : "Open application"}
          </button>
          <button onClick={onStartAnother} style={{ ...css.btnGhost, flex: 1 }} data-testid="office-new-another">
            {lang === "ar" ? "إنشاء معاملة أخرى" : "Create another"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Segmented({ value, onChange, options, css, testId }) {
  return (
    <div style={css.segWrap} data-testid={testId}>
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)} style={css.segBtn(value === o.v)} type="button" data-testid={`${testId}-${o.v}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

const makeStyles = (theme) => ({
  page: { maxWidth: 640, margin: "0 auto", padding: "20px 16px 60px", fontFamily: "'Tajawal','Segoe UI',sans-serif", color: theme.text, position: "relative", zIndex: 1 },
  card: { background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`, borderRadius: 16, padding: 20, marginBottom: 16 },
  h2: { fontSize: 18, fontWeight: 900, color: theme.gold, margin: "0 0 14px" },
  // Theme tokens exposed for downstream components (Stage4 radio cards etc.)
  gold: theme.gold,
  border: theme.border,
  text: theme.text,
  activeBg: theme.name === "dark" ? "rgba(212,175,55,0.10)" : "rgba(184,144,42,0.10)",
  officeBanner: {
    display: "flex", gap: 10, alignItems: "flex-start",
    background: theme.name === "dark" ? "rgba(212,175,55,0.10)" : "rgba(184,144,42,0.10)",
    border: `1px solid ${theme.borderAccent}`,
    color: theme.gold, borderRadius: 10, padding: "10px 14px",
    marginBottom: 14, fontSize: 12, fontWeight: 700, lineHeight: 1.6,
  },
  backToPortal: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "transparent",
    border: `1px solid ${theme.borderSoft}`,
    color: theme.textSoft,
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: 13, fontWeight: 700,
    cursor: "pointer",
    marginBottom: 14,
    transition: "all 150ms ease",
    fontFamily: "inherit",
  },
  tipBox: {
    display: "flex", gap: 10,
    background: theme.name === "dark" ? "rgba(212,175,55,0.08)" : "rgba(184,144,42,0.08)",
    border: `1px solid ${theme.borderAccent}`,
    color: theme.gold, borderRadius: 10, padding: "12px 14px", marginBottom: 16,
  },
  hint: { fontSize: 12, color: theme.textMuted, marginBottom: 14, lineHeight: 1.7 },
  input: {
    width: "100%", padding: "11px 13px", borderRadius: 10,
    border: `1px solid ${theme.inputBorder}`, background: theme.inputBg,
    color: theme.text, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
  },
  segWrap: { display: "flex", gap: 6, padding: 4, background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, borderRadius: 10 },
  segBtn: (active) => ({
    flex: 1, padding: "9px 8px", borderRadius: 7, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12,
    background: active ? theme.goldGradient : "transparent",
    color: active ? (theme.name === "dark" ? "#0a0a0a" : "#fff") : theme.text,
    transition: "all 150ms",
  }),
  factSoft: { padding: 14, background: theme.surface, borderRadius: 10, border: `1px solid ${theme.borderSoft}` },
  memberCard: { background: theme.surface, border: `1px solid ${theme.borderSoft}`, borderRadius: 12, padding: 14, marginBottom: 12 },
  memberHead: { display: "flex", justifyContent: "space-between", marginBottom: 8 },
  removeBtn: {
    background: "transparent", border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, padding: "4px 10px", borderRadius: 6,
    cursor: "pointer", fontSize: 11, fontWeight: 700,
    display: "inline-flex", alignItems: "center", gap: 4,
  },
  btnGhostFull: {
    width: "100%", padding: "12px", background: "transparent",
    border: `1.5px dashed ${theme.borderAccent}`, borderRadius: 10,
    color: theme.gold, fontWeight: 800, cursor: "pointer", fontSize: 13,
  },
  docList: { display: "flex", flexDirection: "column", gap: 10 },
  docItem: { background: theme.surface, border: `1px solid ${theme.borderSoft}`, borderRadius: 10, padding: 12 },
  docHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
  docLabel: { fontSize: 13, fontWeight: 700 },
  tag: { fontSize: 10, fontWeight: 800 },
  docPick: {
    width: "100%", padding: 14, background: "transparent",
    border: `1.5px dashed ${theme.borderAccent}`, borderRadius: 10,
    color: theme.gold, fontWeight: 700, cursor: "pointer", fontSize: 12,
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
  },
  docDone: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 12px",
    background: theme.name === "dark" ? "rgba(34,197,94,0.08)" : "rgba(34,197,94,0.10)",
    border: `1px solid rgba(34,197,94,0.4)`, borderRadius: 8,
    fontSize: 12, color: theme.text,
  },
  toggleCard: { background: theme.surface, border: `1px solid ${theme.borderSoft}`, borderRadius: 12, padding: 16, marginBottom: 12 },
  toggleHead: { display: "flex", alignItems: "center", gap: 14 },
  switch: { position: "relative", display: "inline-block", width: 44, height: 24, cursor: "pointer" },
  slider: (on) => ({
    position: "absolute", inset: 0, borderRadius: 12,
    background: on ? theme.gold : "#999", transition: "all 200ms",
    boxShadow: `inset ${on ? "20px" : "0"} 0 0 0 ${on ? theme.gold : "transparent"}`,
  }),
  policy: { fontSize: 13, lineHeight: 1.8, color: theme.textSoft, marginBottom: 10 },
  policyDetails: { background: theme.surface, border: `1px solid ${theme.borderSoft}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14 },
  policySummary: { cursor: "pointer", fontWeight: 700, color: theme.gold, fontSize: 13 },
  agreeRow: {
    display: "flex", gap: 10, alignItems: "flex-start", padding: 14,
    background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 10, fontSize: 13, lineHeight: 1.6, cursor: "pointer",
  },
  err: {
    background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, padding: 12, borderRadius: 10, fontSize: 13,
    marginBottom: 12, lineHeight: 1.6,
  },
  nav: { display: "flex", gap: 10, marginTop: 16 },
  btnPrimary: {
    flex: 1, padding: "14px 24px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800,
    cursor: "pointer", letterSpacing: "0.4px",
  },
  btnGhost: {
    padding: "14px 24px", background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: 12,
    fontSize: 14, fontWeight: 800, cursor: "pointer",
  },
});
